import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import VerticalTabs from 'components/VerticalTabs';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getTrainingSession,
  updateTrainingSession,
  launchTrainingSession,
  cancelTrainingSession,
} from 'http/trainingSessions';
import { routesAliases } from 'router/routesAliases';
import { actions as modalActions } from 'redux/modal';
import { openEntityLifecycleConfirm } from 'utils';
import { canAssignParticipantsToSession, isCancelledStatus } from 'utils/entityLifecycleStatus';
import { toast } from 'react-toastify';
import { ROLES } from 'appConstants';
import TrainingSessionForm from '../TrainingSessionForm';
import ParticipantsPanel from './ParticipantsPanel';
import SessionFilesPanel from './SessionFilesPanel';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
  summary: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const Edit = ({ routeProps }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const { id } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const backUrl = routesAliases.trainingSessionsList;

  const closeModal = useCallback(() => {
    dispatch(modalActions.closeModal());
  }, [dispatch]);

  const moduleLabel = useMemo(() => {
    if (!sessionData) {
      return '';
    }
    const code = sessionData.trainingModuleCode || '';
    const name = sessionData.trainingModuleName || '';
    if (code && name) {
      return `${code}. ${name}`;
    }
    return name || code;
  }, [sessionData]);

  const sessionTitle = useMemo(() => {
    if (!sessionData) {
      return t(routeProps.title);
    }
    const partVersion = sessionData.trainingPartVersionCode || '';
    const methodLabel = sessionData.trainingMethod
      ? t(`trainingModules.tabs.${sessionData.trainingMethod}`, {
          defaultValue: sessionData.trainingMethod,
        })
      : '';
    const base = `${moduleLabel} ${partVersion}`.trim();
    return methodLabel ? `${base} (${methodLabel})`.trim() : base || t(routeProps.title);
  }, [moduleLabel, routeProps.title, sessionData, t]);

  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: sessionTitle,
      isActive: true,
    },
  ];

  const [fetchState, fetchSession] = useAsyncFn(async sessionId => {
    const result = await getTrainingSession(sessionId);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setSessionData(result);
      setNotFoundError(false);
    } else if (result.statusCode === 404) {
      setNotFoundError(true);
    }
    return result;
  });

  const [submittingState, saveSession] = useAsyncFn(async payload => {
    const result = await updateTrainingSession(id, payload);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setSessionData(result);
      toast.success(t('trainingSessions.saveSuccess'));
    }
    return result;
  });

  const [launchState, launchSession] = useAsyncFn(async () => {
    const result = await launchTrainingSession(id);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setSessionData(result);
    }
    return result;
  }, [id]);

  const [cancelState, cancelSession] = useAsyncFn(async () => {
    const result = await cancelTrainingSession(id);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setSessionData(result);
    }
    return result;
  }, [id]);

  const confirmLaunchSession = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingSessions',
      action: 'launch',
      onConfirm: launchSession,
    });
  }, [closeModal, dispatch, launchSession, t]);

  const confirmCancelSession = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingSessions',
      action: 'cancel',
      onConfirm: cancelSession,
    });
  }, [cancelSession, closeModal, dispatch, t]);

  useEffect(() => {
    setNotFoundError(false);
    setSessionData(null);
    fetchSession(id);
  }, [id]);

  const isAdminViewingGlobal = role === ROLES.ADMIN && sessionData?.scope === 'global';
  const isCancelled = isCancelledStatus(sessionData?.status);
  // Case#4: Admin cannot edit/launch/delete global session metadata or files.
  const isFormReadOnly = isCancelled || isAdminViewingGlobal;
  // Case#6: Admin may manage participants on global sessions (station-scoped on backend).
  const isParticipantsReadOnly = isCancelled;
  const canAssignParticipants = canAssignParticipantsToSession(sessionData?.status);

  const tabsData = useMemo(() => {
    if (!sessionData) {
      return [];
    }

    return [
      {
        label: t('trainingSessions.tabs.general'),
        alias: 'general',
        Component: (
          <TrainingSessionForm
            mode="edit"
            initialValues={sessionData}
            backUrl={backUrl}
            submitting={submittingState.loading || launchState.loading || cancelState.loading}
            onSubmitForm={saveSession}
            onLaunch={confirmLaunchSession}
            onCancelEntity={confirmCancelSession}
            readOnly={isFormReadOnly}
          />
        ),
      },
      {
        label: t('trainingSessions.tabs.participants'),
        alias: 'participants',
        Component: (
          <ParticipantsPanel
            trainingSessionId={id}
            deliveryMethod={sessionData.deliveryMethod}
            hasMoodleLink={sessionData.hasMoodleLink}
            moodleLmsCourseId={sessionData.moodleLmsCourseId}
            trainingMethod={sessionData.trainingMethod}
            readOnly={isParticipantsReadOnly}
            canAssign={canAssignParticipants}
          />
        ),
      },
      {
        label: t('trainingSessions.tabs.files'),
        alias: 'files',
        Component: (
          <SessionFilesPanel
            trainingSessionId={id}
            trainingPartId={sessionData.trainingPartId}
            canManageVisibility={!isFormReadOnly}
            canAddFiles={!isFormReadOnly}
            isReadOnly={isFormReadOnly}
          />
        ),
      },
    ];
  }, [
    backUrl,
    canAssignParticipants,
    cancelState.loading,
    confirmCancelSession,
    confirmLaunchSession,
    id,
    isFormReadOnly,
    isParticipantsReadOnly,
    launchState.loading,
    saveSession,
    sessionData,
    submittingState.loading,
    t,
  ]);

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !sessionData}
    >
      {sessionData ? (
        <>
          <Box className={classes.summary}>
            <Typography variant="body2">
              {t('trainingSessions.summary', {
                module: moduleLabel,
                part: sessionData.trainingMethod
                  ? `${sessionData.trainingPartVersionCode || ''} (${t(
                      `trainingModules.tabs.${sessionData.trainingMethod}`,
                      {
                        defaultValue: sessionData.trainingMethod,
                      },
                    )})`.trim()
                  : sessionData.trainingPartVersionCode,
                method: t(`trainingSessions.deliveryMethods.${sessionData.deliveryMethod}`),
              })}
            </Typography>
          </Box>
          <VerticalTabs
            tabsData={tabsData}
            activeTab={activeTab}
            onTabChange={(event, { tabIndex }) => setActiveTab(tabIndex)}
          />
        </>
      ) : null}
    </PageLayout>
  );
};

Edit.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default Edit;
